import { useState, useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'playcast_last_welcomed_user';
const LOG_PREFIX = '[OnboardingWelcome]';

/**
 * Determines whether the onboarding welcome screen should be shown.
 *
 * Shows welcome when currentUserId differs from the last-welcomed userId
 * stored in localStorage (covers fresh installs and user switches).
 *
 * After markSeen(), stores the currentUserId so subsequent launches skip welcome.
 */
export function useOnboardingWelcome(userId: string | null): {
  needsWelcome: boolean;
  evaluated: boolean;
  markSeen: () => void;
} {
  const [needsWelcome, setNeedsWelcome] = useState(false);
  const [evaluated, setEvaluated] = useState(false);
  const lastEvaluatedUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    if (lastEvaluatedUserRef.current === userId) return;
    lastEvaluatedUserRef.current = userId;

    try {
      const storedUser = localStorage.getItem(STORAGE_KEY);
      if (storedUser !== userId) {
        console.debug(LOG_PREFIX, `User changed (${storedUser} → ${userId}) — welcome gate open`);
        setNeedsWelcome(true);
      } else {
        console.debug(LOG_PREFIX, `Same user ${userId} — welcome gate skipped`);
        setNeedsWelcome(false);
      }
    } catch {
      console.debug(LOG_PREFIX, 'localStorage unavailable — defaulting to needs welcome');
      setNeedsWelcome(true);
    }
    setEvaluated(true);
  }, [userId]);

  const markSeen = useCallback(() => {
    if (!userId) return;
    console.debug(LOG_PREFIX, `Marking welcome as seen for user ${userId}`);
    try {
      localStorage.setItem(STORAGE_KEY, userId);
    } catch { /* localStorage unavailable */ }
    setNeedsWelcome(false);
  }, [userId]);

  return { needsWelcome, evaluated, markSeen };
}
